import * as XLSX from 'xlsx';
import React from 'react';
import { saveAs } from 'file-saver';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { getHHMMSSFromSeconds } from '../../../utils/TimeConversionFunctions';
import { getMonthlyPayrollReport } from '../../Services/ReportService';
import {
  Box,
  CircularProgress,
  IconButton,
  ThemeProvider,
  Typography,
} from '@mui/material';
import MainTheme from '../../../MainTheme';
import moment from 'moment';

const ALLOWABLE_REPORT_OPTIONS = ["Monthly Timings Report", "Monthly Timings Report (old)"]

const XlsxDownloadButton = (props) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { startDate, endDate, userList, empStatus, activeTab, filterData, sortModel } = props;
  let i = 0;

  const buildNewPayrollRows = (content) => {
    let rowData = [];
    if (!Array.isArray(content)) {
      return rowData;
    }
    for (let idx = 0; idx < content.length; idx++) {
      const personData = content[idx];
      const numberOfMonths = personData?.monthlyTimingsDTOList?.length || 0;
      for (let j = numberOfMonths - 1; j >= 0; j--) {
        const monthSummary =
          personData.monthlyTimingsDTOList[numberOfMonths - 1 - j];
        const currentMonth = new Date(monthSummary.startDate);
        const monthName = currentMonth.toLocaleString('default', {
          month: 'long',
        });
        const year = currentMonth.toLocaleString('default', {
          year: 'numeric',
        });
        let newRow = {
          srNo: idx * numberOfMonths + numberOfMonths - j,
          employeeCode: personData.employeeCode,
          fullName: personData.fullName,
          officeLocation: monthSummary.officeLocation || '',
          month: `${monthName} ${year}`,
          deficitHrs: getHHMMSSFromSeconds(monthSummary.deficitHrs / 1000),
          totalOfficeHours: getHHMMSSFromSeconds(
            monthSummary.totalOfficeHours / 1000,
          ),
          expectedHours: getHHMMSSFromSeconds(monthSummary.expectedHours / 1000),
          totalWfhHours: getHHMMSSFromSeconds(monthSummary.totalWfhHours / 1000),
          totalExternalHours: getHHMMSSFromSeconds(
            monthSummary.totalExternalHours / 1000,
          ),
          weekendHours: getHHMMSSFromSeconds(monthSummary.weekendHours / 1000),
          totalWeekendWorkingHours: getHHMMSSFromSeconds(
            (monthSummary.totalWeekendWorkingHours || 0) / 1000,
          ),
          workFromHomeDays: monthSummary.workFromHomeDays,
          totalHours: getHHMMSSFromSeconds(monthSummary.totalHours / 1000),
          autoConvertedLeaves: monthSummary.autoConvertedLeaves,
          manualCasualLeaves: monthSummary.manualCasualLeaves,
          leaves: monthSummary.leaves,
        };

        const breakdown = monthSummary.leaveCategoryBreakdown;
        if (breakdown && typeof breakdown === 'object') {
          Object.entries(breakdown).forEach(([categoryName, count]) => {
            newRow['leaveBreakdown_' + categoryName] = count ?? 0;
          });
        }
        rowData.push(newRow);
      }
    }
    return rowData;
  };

  const getUserData = (data) => {
    if (!Array.isArray(data)) {
      console.error('Data is not an array in getUserData');
      return [];
    }

    let newData = [];
    data.map((personData) => {
      let modifiedPersonData = {};
      modifiedPersonData = {
        ...modifiedPersonData,
        'S.No.': ++i,
        'Employee Id': personData.employeeCode,
        // 'Tenant Id': personData.tenantId,
        'Full Name': personData.fullName,
        'Office Location':
          personData.monthlyTimingsDTOList?.[
            personData.monthlyTimingsDTOList.length - 1
          ]?.officeLocation || '',
        'Date Of Joining': personData.dateOfJoining || '',
        'Date Of Leaving': personData.dateOfLeaving || '',
      };
      // Apply left alignment to each value in the row
      modifiedPersonData = Object.keys(modifiedPersonData).reduce(
        (acc, key) => {
          acc[key] = {
            v: modifiedPersonData[key],
            s: { alignment: { horizontal: 'left' } },
          };
          return acc;
        },
        {},
      );
      newData.push(modifiedPersonData);
    });
    return newData;
  };
  const getPastMonthsData = (data) => {
    let newData = [];
    data.map((personData) => {
      let modifiedPersonData = [];
      const numberOfMonths = personData.monthlyTimingsDTOList.length;
      for (let i = 0; i < numberOfMonths - 1; i++) {
        const currentMonthData = personData.monthlyTimingsDTOList[i];
        modifiedPersonData = [
          ...modifiedPersonData,
          getHHMMSSFromSeconds(currentMonthData.deficitHrs / 1000),
        ];
      }
      newData.push(modifiedPersonData);
    });
    return newData;
  };
  const getMonthNamesAndYearData = (data) => {
    let newData = [];
    const personData = data[0];
    let monthData = [];
    let yearData = [];
    let monthsCountInYear = [];
    let count = 0;
    let prevYear = -1;
    const numberOfMonths = personData.monthlyTimingsDTOList.length;
    for (let i = 0; i < numberOfMonths; i++) {
      const currentMonthData = personData.monthlyTimingsDTOList[i];
      const currentMonth = new Date(currentMonthData.startDate);
      const monthName = currentMonth.toLocaleString('default', {
        month: 'long',
      });
      monthData = [...monthData, monthName];
      const year = currentMonth.toLocaleString('default', {
        year: 'numeric',
      });
      if (!yearData.includes(year)) {
        yearData = [...yearData, year];
      }
      if (prevYear !== year) {
        if (prevYear !== -1) {
          monthsCountInYear = [...monthsCountInYear, count];
        }
        prevYear = year;
        count = 1;
      } else {
        count++;
      }
    }
    monthsCountInYear = [...monthsCountInYear, count];
    newData.push(monthData);
    newData.push(yearData);
    newData.push(monthsCountInYear);
    return newData;
  };

  const createMerger = (monthsCountInYear) => {
    let modifiedPersonData = [];
    // Col 0 = S.No.; cols 1-5 = Employee Details (Id, Name, Location, DOJ, DOL)
    let start = 6;
    for (let i = 0; i < monthsCountInYear.length; i++) {
      let end = start + monthsCountInYear[i];
      if (i === monthsCountInYear.length - 1) {
        // Latest month detail columns: 12 (was 10; + Auto Converted + Manual Casual)
        end = end + 10;
      }
      let mergeToAdd = {
        s: { r: 0, c: start },
        e: { r: 0, c: end },
      };
      modifiedPersonData = [...modifiedPersonData, mergeToAdd];

      start = end + 1;
    }
    return modifiedPersonData;
  };

  const getCurrentMonthData = (data) => {
    let newData = [];
    data.map((personData) => {
      let modifiedPersonData = {};
      const numberOfMonths = personData.monthlyTimingsDTOList.length;
      const currentMonthData =
        personData.monthlyTimingsDTOList[numberOfMonths - 1];
      modifiedPersonData = {
        ...modifiedPersonData,
        'Office Hours': getHHMMSSFromSeconds(
          currentMonthData.totalOfficeHours / 1000,
        ),
        'WFH Hours': getHHMMSSFromSeconds(
          currentMonthData.totalWfhHours / 1000,
        ),
        'External Hours': getHHMMSSFromSeconds(
          currentMonthData.totalExternalHours / 1000,
        ),
        'Weekend Hours': getHHMMSSFromSeconds(
          currentMonthData.weekendHours / 1000,
        ),
        'WFH Days': currentMonthData.workFromHomeDays,
        'Total Hours': getHHMMSSFromSeconds(currentMonthData.totalHours / 1000),
        'Average Hours': getHHMMSSFromSeconds(
          (currentMonthData.totalHours / 1000) / ((currentMonthData.expectedHours / 3600000) / 9),
        ),
        'Expected Hours': getHHMMSSFromSeconds(
          currentMonthData.expectedHours / 1000,
        ),
        'Deficit Hours': getHHMMSSFromSeconds(
          currentMonthData.deficitHrs / 1000,
        ),
        'Auto Converted Leaves': currentMonthData.autoConvertedLeaves,
        'Manual Casual Leave': currentMonthData.manualCasualLeaves,
        Leaves: currentMonthData.leaves,
        'Cumulative Deficit Hours': getHHMMSSFromSeconds(
          personData.yearToDateDeficitHours / 1000,
        ),
        'Cumulative Leaves': personData.yearToDateLeaves,
        // Dynamic cumulative per-category leave columns
        ...(personData.yearToDateLeaveCategoryBreakdown
          ? Object.fromEntries(
              Object.entries(personData.yearToDateLeaveCategoryBreakdown).map(
                ([name, count]) => ['Cumulative ' + name, count ?? 0]
              )
            )
          : {}),
        'Cumulative WFH Hours': getHHMMSSFromSeconds(
          personData.yearToDateWfhHrs / 1000,
        ),
        'Cumulative WFH Days Taken': personData.yearToDateWfhDaysTaken,
        'Cumulative WFH Days Quota': personData.yearToDateWfhDaysQuota,
        'Cumulative Office Hours': getHHMMSSFromSeconds(
          personData.yearToDateOfficeHours / 1000,
        ),
        'Cumulative External Hours': getHHMMSSFromSeconds(
          personData.yearToDateExternalHours / 1000,
        ),
        'Cumulative Manual Casual Leave':
          personData.yearToDateManualCasualLeave,
        'Cumulative Auto Converted Leave':
          personData.yearToDateAutoConvertedLeave,

      };
      newData.push(modifiedPersonData);
    });
    return newData;
  };

  const getDownloadableData = async () => {
    const reportData = await getMonthlyPayrollReport(
      moment(new Date(startDate)).format('YYYY-MM-DD'),
      moment(new Date(endDate)).format('YYYY-MM-DD'),
      userList,
      filterData?.columnFilters || [],
      0,
      -1,
      activeTab,
    );
    if (reportData.isError === false) {
      return reportData?.data?.data?.content || [];
    }
    return [];
  };

  const generateXlsxFileObject = async (ws, reportType) => {

    /* add to workbook */
    var wb = XLSX.utils.book_new();
    // Prepare the workbook and file download logic
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Report');
    var wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

    /* generate a download */
    function s2ab(s) {
      var buf = new ArrayBuffer(s.length);
      var view = new Uint8Array(buf);
      for (var i = 0; i != s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    }

    const dateString = new Date().toDateString();
    const fileName = `${empStatus}_${reportType}_${dateString}.xlsx`;
    saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), [
      fileName,
    ]);
  };

  const handleDownload = async () => {
    // Check for both report types
    if (!ALLOWABLE_REPORT_OPTIONS.includes(filterData.reportType) || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      let data = await getDownloadableData();
      if (!Array.isArray(data) || data.length === 0) {
        return;
      }

      if (sortModel?.length > 0) {
        const data1 = [...data];

        data1.sort((a, b) => {
          for (let i = 0; i < sortModel.length; i++) {
            const { colId, sort } = sortModel[i];
            const result =
              (a[colId] > b[colId] ? 1 : a[colId] < b[colId] ? -1 : 0) *
              (sort === 'asc' ? 1 : -1);

            // Return the result if comparison is not equal
            if (result !== 0) {
              return result;
            }
          }
          //return 0;
        });

        data = data1;
      }

      /* make the worksheet */
      var ws = XLSX.utils.json_to_sheet([]);
      let merge;

      if (filterData.reportType === 'Monthly Timings Report (old)') {
        let userData = getUserData(data);
        let pastMonthsData = getPastMonthsData(data);
        let currentMonthData = getCurrentMonthData(data);
        let monthNamesData = getMonthNamesAndYearData(data)[0];
        let yearsData = getMonthNamesAndYearData(data)[1];
        let monthsCountInYear = getMonthNamesAndYearData(data)[2];
        const numberOfMonths = data[0].monthlyTimingsDTOList.length;
        const dynamicMerge = createMerger(monthsCountInYear);

        XLSX.utils.sheet_add_aoa(ws, [['S.No.']], { origin: { r: 0, c: 0 } });
        XLSX.utils.sheet_add_aoa(ws, [['Employee Details']], {
          origin: { r: 0, c: 1 },
        });
        XLSX.utils.sheet_add_json(ws, userData, { origin: { r: 2, c: 0 } });
        for (let i = 0; i < yearsData.length; i++) {
          XLSX.utils.sheet_add_aoa(ws, [[yearsData[i]]], {
            origin: dynamicMerge[i].s,
          });
        }
        // Month columns start after S.No. + 5 employee detail fields
        const monthStartCol = 6;
        const latestMonthDetailCols = 12;
        const cumulativeCols = 11;
        XLSX.utils.sheet_add_aoa(ws, [monthNamesData], {
          origin: { r: 1, c: monthStartCol },
        });
        XLSX.utils.sheet_add_aoa(ws, pastMonthsData, {
          origin: { r: 3, c: monthStartCol },
        });
        XLSX.utils.sheet_add_json(ws, currentMonthData, {
          origin: { r: 2, c: monthStartCol + numberOfMonths - 1 },
        });
        for (let i = 0; i < numberOfMonths - 1; i++) {
          XLSX.utils.sheet_add_aoa(ws, [['Deficit Hours']], {
            origin: { r: 2, c: monthStartCol + i },
          });
        }

        const cumulativeStartCol =
          monthStartCol + numberOfMonths - 1 + latestMonthDetailCols;
        XLSX.utils.sheet_add_aoa(ws, [['Cumulative Details']], {
          origin: { r: 0, c: cumulativeStartCol },
        });
        merge = [
          //S.No.
          {
            s: { r: 0, c: 0 },
            e: { r: 2, c: 0 },
          },
          //Employee Details
          {
            s: { r: 0, c: 1 },
            e: { r: 1, c: 5 },
          },
          //Last Month
          {
            s: { r: 1, c: monthStartCol + numberOfMonths - 1 },
            e: {
              r: 1,
              c:
                monthStartCol +
                numberOfMonths -
                1 +
                latestMonthDetailCols -
                1,
            },
          },
          //Cumulative Details
          {
            s: { r: 0, c: cumulativeStartCol },
            e: { r: 1, c: cumulativeStartCol + cumulativeCols - 1 },
          },
        ];

        const concatenatedMerge = [...merge, ...dynamicMerge];
        ws['!merges'] = concatenatedMerge;

      } else {

        const newPayrollRowData = buildNewPayrollRows(data);
        
        // Collect all leave category names from the first row that has them
        const leaveCategories = [];
        const firstWithBreakdown = newPayrollRowData?.find(
          (emp) => emp && Object.keys(emp).some((k) => k.startsWith('leaveBreakdown_'))
        );
        if (firstWithBreakdown) {
          Object.keys(firstWithBreakdown)
            .filter((k) => k.startsWith('leaveBreakdown_'))
            .forEach((k) => leaveCategories.push(k.replace('leaveBreakdown_', '')));
        }

        let startingRow = 0;
        XLSX.utils.sheet_add_aoa(
          ws,
          [
            [
              'S.No.',
              'Employee ID',
              'Full Name',
              'Office Location',
              'Month',
              'Deficit Hrs',
              'office Hrs',
              'Expected Hrs',
              'WFH Hrs',
              'External Hours',
              'Weekend Hrs',
              'Weekend Working Hrs',
              'WFH Days',
              'Total Hrs',
              'Auto Converted Leaves',
              'Manual Casual Leave',
              'Leaves',
              // Dynamic leave category headers
              ...leaveCategories,
            ],
          ],
          { origin: startingRow },
        );
        startingRow += 1;

        newPayrollRowData.forEach((employee) => {
          const rowData = [
            employee.srNo,
            employee.employeeCode,
            employee.fullName,
            employee.officeLocation,
            employee.month,
            employee.deficitHrs,
            employee.totalOfficeHours,
            employee.expectedHours,
            employee.totalWfhHours,
            employee.totalExternalHours,
            employee.weekendHours,
            employee.totalWeekendWorkingHours,
            employee.workFromHomeDays,
            employee.totalHours,
            employee.autoConvertedLeaves,
            employee.manualCasualLeaves,
            employee.leaves,
            // Dynamic leave category values
            ...leaveCategories.map((cat) => employee['leaveBreakdown_' + cat] ?? 0),
          ];

          // Left alignment for all cells
          rowData.forEach((value, index) => {
            rowData[index] = {
              v: value,
              s: { alignment: { horizontal: 'left' } },
            };
          });

          // Add the row to the sheet
          XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: startingRow });
          startingRow += 1; // Move to the next row after each employee
        });
      }
      generateXlsxFileObject(ws, filterData.reportType);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ThemeProvider theme={MainTheme}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mr: 1,
          }}
        >
          {isDownloading && (
            <>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                The reports you requested are downloading, please wait.
              </Typography>
            </>
          )}
          <IconButton
            color="black"
            onClick={handleDownload}
            size="large"
            disabled={isDownloading}
          >
            <SimCardDownloadIcon fontSize="large" />
          </IconButton>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default XlsxDownloadButton;
